import {socialCount, truncateDescription} from '@github-ui/code-view-sidebar/utils'
import {NotificationsSubscriptionsMenu} from '@github-ui/notifications-subscriptions-menu'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {HeaderSponsorButton} from './HeaderSponsorButton'
import {
  type Icon,
  AccessibilityInsetIcon,
  CodeOfConductIcon,
  EyeIcon,
  GitBranchIcon,
  GlobeIcon,
  LawIcon,
  LinkIcon,
  LockIcon,
  MirrorIcon,
  NoteIcon,
  PeopleIcon,
  PulseIcon,
  RepoForkedIcon,
  RepoIcon,
  RepoTemplateIcon,
  ShieldLockIcon,
  StarIcon,
  TagIcon,
} from '@primer/octicons-react'
import {Link, Truncate} from '@primer/react'
import {SoftNavOnlyLink as RouterLink} from '@github-ui/react-router'
import {isUIService} from '@github-ui/runtime-environment'
import type {HeaderResponse, RepoPaths} from '../types'
import {ForkButton} from '@github-ui/repo-fork-button'
import {StarButton} from '@github-ui/repo-star-button'
import {TemplateButton} from '@github-ui/repo-template-button'
import {useErrorMessage} from '../utils/use-error-message'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {useBranchAndTagCount} from '@github-ui/code-view-shared/contexts/BranchAndTagContext'

function pluralize(count: number, singular: string, plural?: string): string {
  return count === 1 ? singular : (plural ?? `${singular}s`)
}

function repoTypeLabel(
  repo: Pick<HeaderResponse['repo'], 'isMirror' | 'isTemplate' | 'isPrivate' | 'visibilityLabel'>,
): {icon: typeof RepoIcon; text: string} {
  const {isMirror, isTemplate, isPrivate, visibilityLabel} = repo
  if (isMirror) return {icon: MirrorIcon, text: `${visibilityLabel} repository`}
  if (isTemplate) return {icon: RepoTemplateIcon, text: `${visibilityLabel} repository`}
  if (isPrivate) return {icon: LockIcon, text: `${visibilityLabel} repository`}
  return {icon: GlobeIcon, text: `${visibilityLabel} repository`}
}

export type RepoMobileDetailsProps = Pick<
  HeaderResponse,
  | 'description'
  | 'website'
  | 'stargazerCount'
  | 'forksCount'
  | 'watcherCount'
  | 'stargazersPath'
  | 'forkNetworkPath'
  | 'watchersPath'
  | 'showInsights'
  | 'ownerLogin'
  | 'repoName'
  | 'repo'
  | 'star'
  | 'fork'
  | 'watch'
  | 'viewer'
  | 'showSponsorButton'
  | 'showTemplateButton'
  | 'sections'
  | 'isOrg'
  | 'customPropertiesPath'
> & {
  paths: RepoPaths
  hasAccessToCodespaces?: boolean
  /** Whether the repository has a code of conduct file. */
  hasCodeOfConduct?: boolean
  /** Whether the repository has a contributing guide. */
  hasContributing?: boolean
  /** Whether the repository has a security policy. */
  hasSecurityPolicy?: boolean
  /** Whether the repository has an accessibility statement. */
  hasAccessibility?: boolean
}

/** A muted icon + optional bold count + label link used for repository stats. */
function StatLink({href, icon: Icon, count, label}: {href: string; icon: Icon; count?: number; label: string}) {
  return (
    <Link as={RouterLink} muted to={href} className="d-block mr-2">
      <Icon size={16} className="mr-2" />
      {count !== undefined ? (
        <>
          <span className="text-bold">{socialCount(count)}</span> {label}
        </>
      ) : (
        label
      )}
    </Link>
  )
}

function CommunityLink({
  href,
  icon: Icon,
  label,
  analyticsLabel,
  'data-testid': dataTestId,
}: {
  href: string
  icon: Icon
  label: string
  analyticsLabel: string
  'data-testid'?: string
}) {
  const {sendClickAnalyticsEvent} = useClickAnalytics()

  return (
    <div className="mb-2 d-flex flex-items-center" data-testid={dataTestId}>
      <Icon className="color-fg-muted flex-shrink-0 mr-2" />
      <Link
        muted
        href={href}
        onClick={() =>
          sendClickAnalyticsEvent({
            category: 'REPOSITORY_OVERVIEW',
            action: 'click',
            label: `location:sidebar;file:${analyticsLabel}`,
          })
        }
      >
        {label}
      </Link>
    </div>
  )
}

/** Mobile-only repository details shown below the title on narrow screens. */
export function RepoMobileDetails({
  repo,
  fork,
  star,
  watch,
  viewer,
  paths,
  isOrg,
  ownerLogin,
  repoName,
  showSponsorButton,
  showTemplateButton,
  hasAccessToCodespaces,
  description,
  website,
  stargazerCount,
  forksCount,
  watcherCount,
  stargazersPath,
  forkNetworkPath,
  watchersPath,
  showInsights,
  sections,
  customPropertiesPath,
  hasCodeOfConduct,
  hasContributing,
  hasSecurityPolicy,
  hasAccessibility,
}: RepoMobileDetailsProps) {
  const repoNameWithOwner = paths.repoNameWithOwner
  const showBanner = useErrorMessage()

  const typeInfo = repoTypeLabel(repo)
  const tagCount = typeof sections.releases === 'object' ? sections.releases.tagCount : undefined
  const branchAndTagCount = useBranchAndTagCount()
  const branchCount = branchAndTagCount?.branches
  const getHovercardAttributes = useGetHovercardAttributesForType()

  return (
    <div className="d-block d-md-none" data-testid="repo-mobile-details">
      {/* Functional buttons row */}
      <div className="d-flex gap-2 tmp-mb-3 flex-wrap">
        <div className="d-flex flex-row gap-2" data-testid="responsive-social-buttons">
          {viewer.isLoggedIn && watch.canWatch && (
            <NotificationsSubscriptionsMenu
              repositoryId={watch.watchData.repositoryId}
              repositoryName={watch.watchData.repositoryName}
              watchersCount={watch.watchData.watchersCount}
              subscriptionType={watch.watchData.subscriptionType}
              subscribableThreadTypes={watch.watchData.subscribableThreadTypes}
              showLabelSubscriptions={watch.watchData.showLabelSubscriptions}
              subscribedLabels={watch.watchData.subscribedLabels}
            />
          )}
          <ForkButton
            forkCount={forksCount}
            canFork={fork.canFork}
            forkabilityError={fork.forkabilityError}
            repoNameWithOwner={repoNameWithOwner}
            forkSelectPath={paths.forkSelectPath}
            forksApiPath={paths.forksApiPath}
            isLoggedIn={viewer.isLoggedIn}
            size="medium"
          />

          <StarButton
            buttonType="icon-button"
            size="medium"
            starred={star.viewerHasStarred}
            starCount={stargazerCount}
            starPath={paths.starPath}
            unstarPath={paths.unstarPath}
            canStar={star.canStar}
            isLoggedIn={viewer.isLoggedIn}
            listsApiPath={paths.listsApiPath}
            listsUpdatePath={paths.listsUpdatePath}
            repoNameWithOwner={paths.repoNameWithOwner}
            emuContributionBlocked={viewer.emuContributionBlocked}
          />
          {showSponsorButton && (
            <HeaderSponsorButton
              ownerLogin={ownerLogin}
              repoName={repoName}
              repoNameWithOwner={repoNameWithOwner}
              isOrgOwned={isOrg}
              size="medium"
            />
          )}
        </div>
        {showTemplateButton && repo.isTemplate && (
          <div className="d-flex flex-row gap-2">
            <TemplateButton
              templatePath={paths.templatePath}
              showTemplateButton={showTemplateButton}
              hasCodespacesAccess={hasAccessToCodespaces ?? false}
              size="medium"
              onError={error => {
                const message = error.message || 'Unable to create template. Please try again.'
                showBanner(message)
              }}
            />
          </div>
        )}
      </div>

      {/* Description */}
      {description && (
        <p className="f4 tmp-mb-3 color-fg-muted" data-testid="mobile-description">
          {truncateDescription(description)}
        </p>
      )}

      {/* License */}
      {repo.license && (
        <div className="mb-2 d-flex flex-items-center">
          <LawIcon className="color-fg-muted flex-shrink-0 mr-2" />
          <span className="color-fg-muted">{repo.license.name}</span>
        </div>
      )}

      {/* Website */}
      {website && (
        <div className="mb-2 d-flex flex-items-center">
          <LinkIcon className="flex-shrink-0 mr-2" />

          <Truncate as="span" title={website} maxWidth="none">
            <Link href={website} target="_blank" rel="noopener noreferrer" className="text-bold">
              {website.replace(/^https?:\/\//, '')}
            </Link>
          </Truncate>
        </div>
      )}

      {/* Community health files */}
      {hasCodeOfConduct && (
        <CommunityLink
          href="#coc-ov-file"
          icon={CodeOfConductIcon}
          label="Code of conduct"
          analyticsLabel="code of conduct"
          data-testid="mobile-code-of-conduct"
        />
      )}

      {hasContributing && (
        <CommunityLink
          href="#contributing-ov-file"
          icon={PeopleIcon}
          label="Contributing"
          analyticsLabel="contributing"
          data-testid="mobile-contributing"
        />
      )}

      {hasSecurityPolicy && (
        <CommunityLink
          href="#security-ov-file"
          icon={ShieldLockIcon}
          label="Security policy"
          analyticsLabel="security policy"
          data-testid="mobile-security-policy"
        />
      )}

      {hasAccessibility && (
        <CommunityLink
          href="#accessibility-ov-file"
          icon={AccessibilityInsetIcon}
          label="Accessibility"
          analyticsLabel="accessibility"
          data-testid="mobile-accessibility"
        />
      )}

      {/* Stats */}
      <ul
        className="d-flex flex-wrap mb-2 gap-2 list-style-none pl-0"
        data-testid="repo-responsive-details"
        aria-label="Repository details"
      >
        <li>
          <StatLink
            href={stargazersPath}
            icon={StarIcon}
            count={stargazerCount}
            label={pluralize(stargazerCount, 'star')}
          />
        </li>
        <li>
          <StatLink
            href={forkNetworkPath}
            icon={RepoForkedIcon}
            count={forksCount}
            label={pluralize(forksCount, 'fork')}
          />
        </li>
        <li>
          <StatLink href={watchersPath} icon={EyeIcon} count={watcherCount} label="watching" />
        </li>
        <li>
          <StatLink
            href={paths.branchesPath}
            icon={GitBranchIcon}
            count={branchCount}
            label={branchCount === undefined ? 'Branches' : pluralize(branchCount, 'branch', 'branches')}
          />
        </li>
        <li>
          <StatLink href={paths.tagsPath} icon={TagIcon} count={tagCount} label={pluralize(tagCount ?? 0, 'tag')} />
        </li>
        {showInsights && (
          <li>
            <StatLink href={`${paths.repoUrl}/pulse`} icon={PulseIcon} label="Activity" />
          </li>
        )}
        {isOrg && customPropertiesPath && (
          <li>
            <StatLink href={customPropertiesPath} icon={NoteIcon} label="Custom properties" />
          </li>
        )}
      </ul>

      {/* Repository type indicator */}
      <div className="d-flex color-fg-muted flex-wrap" data-testid="repo-type-indicator">
        <span className="d-inline-flex flex-items-center flex-shrink-0 mr-1">
          <typeInfo.icon size={16} />
          <span className="ml-1">{typeInfo.text}</span>
        </span>
        {repo.isFork && repo.parentRepo && (
          <span>
            {' · Forked from '}
            <Link
              as={RouterLink}
              to={repo.parentRepo.url}
              inline
              reloadDocument={!isUIService}
              {...getHovercardAttributes('repository', {
                owner: repo.parentRepo.ownerLogin,
                repo: repo.parentRepo.name,
              })}
            >
              {repo.parentRepo.ownerLogin}/{repo.parentRepo.name}
            </Link>
          </span>
        )}
        {repo.isMirror && repo.mirrorUrl && (
          <span>
            {' · Mirrored from '}
            <Link as={RouterLink} to={repo.mirrorUrl} inline reloadDocument>
              {repo.mirrorUrl}
            </Link>
          </span>
        )}
      </div>
    </div>
  )
}
